import type { DocumentFieldCorrectedAction } from '@Application/Beneficiaries/Documents/Actions/documentFieldCorrected';
import type { DocumentFieldCorrectionFailedAction } from '@Application/Beneficiaries/Documents/Actions/documentFieldCorrectionFailed';
import type { CorrectDocumentFieldValue } from '@Application/Beneficiaries/Documents/Commands/CorrectDocumentField';
import CorrectDocumentField from '@Application/Beneficiaries/Documents/Commands/CorrectDocumentField';
import correctDocumentField from '@Application/Beneficiaries/Documents/correctDocumentField';
import useDocumentRoute from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useDocumentRoute';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';

export type FieldCorrection = {
	readonly correct: (
		value: CorrectDocumentFieldValue,
	) => Promise<DocumentFieldCorrectedAction | DocumentFieldCorrectionFailedAction>;
	readonly isSubmitting: boolean;
};

/**
 * The single place that builds a `CorrectDocumentField` Command and dispatches it — shared by the
 * free-text draft editor and the immediate options/boolean toggles, which otherwise duplicated the
 * exact same construction. Reads the route ids itself, rather than taking them as a prop, so its
 * callers stay simple leaves. Returns the dispatch promise as-is: each caller keeps its own `.then` /
 * `.catch`, since a failed submission means different things to a draft (reapply `fieldErrors`) and to
 * an immediate toggle (`ignoreHandledError`).
 */
const useFieldCorrection = (fieldKey: string): FieldCorrection => {
	const dispatch = useDispatch();
	const { facilityId, subject, documentId } = useDocumentRoute();
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const correct = React.useCallback(
		(value: CorrectDocumentFieldValue) => {
			setIsSubmitting(true);

			return dispatch(
				correctDocumentField(
					new CorrectDocumentField(facilityId, subject, documentId, fieldKey, value),
				),
			).finally(() => {
				setIsSubmitting(false);
			});
		},
		[dispatch, facilityId, subject, documentId, fieldKey],
	);

	return { correct, isSubmitting };
};

export default useFieldCorrection;
